import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { searchBins, recordEvent } from '../lib/api.js';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

function BinFinder() {
  const [zip, setZip] = useState(() => window.localStorage.getItem('pact-preferred-zip') || '');
  const [radius, setRadius] = useState(25);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (zip) {
      window.localStorage.setItem('pact-preferred-zip', zip);
    }
  }, [zip]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!zip) {
      setError('Enter a ZIP code to search');
      return;
    }
    setError('');
    setLoading(true);
    setResults(null);

    try {
      const data = await searchBins({ zip, radiusMi: radius, limit: 25 });
      setResults(data);
      await recordEvent({
        eventType: 'bin_search_page',
        payload: { zip, radius, count: data?.count || 0 },
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const hasToken = Boolean(MAPBOX_TOKEN);

  return (
    <div className="space-y-10">
      <header className="space-y-3">
        <h1 className="text-4xl font-semibold text-primary">Find a Pact drop-off bin</h1>
        <p className="max-w-2xl text-sm text-neutral">
          Enter your ZIP code to see Pact partner locations near you. Each listing highlights the hosting brand, address, and distance so you can plan your next drop-off with ease.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="form-control">
              <label className="label" htmlFor="zip">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">ZIP code</span>
              </label>
              <input
                id="zip"
                className="input input-bordered"
                value={zip}
                onChange={(event) => setZip(event.target.value)}
                placeholder="e.g. 11222"
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="radius">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">Radius (miles)</span>
              </label>
              <input
                id="radius"
                type="range"
                min="5"
                max="100"
                step="5"
                value={radius}
                onChange={(event) => setRadius(Number(event.target.value))}
                className="range range-primary"
              />
              <span className="mt-1 text-xs text-neutral">Showing bins within {radius} miles</span>
            </div>
            <div className="flex items-end">
              <button type="submit" className="btn btn-primary rounded-full" disabled={loading}>
                {loading ? 'Searching…' : 'Search bins'}
              </button>
            </div>
          </div>
          {error ? (
            <div className="alert alert-error text-sm">
              <span>{error}</span>
            </div>
          ) : null}
          {!hasToken ? (
            <div className="alert alert-warning text-xs">
              <span>
                Map preview unavailable because <code>VITE_MAPBOX_TOKEN</code> is not configured. Results will still list Pact locations.
              </span>
            </div>
          ) : null}
        </div>
      </form>

      {loading ? (
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body text-sm text-neutral">Looking for Pact bins near {zip}…</div>
        </div>
      ) : null}

      {results ? <BinResults results={results} hasToken={hasToken} /> : null}
    </div>
  );
}

function BinResults({ results, hasToken }) {
  if (!results || !results.results?.length) {
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-3 text-sm text-neutral">
          <p>No Pact bins found within the selected radius. Try expanding your radius or request a mail-back label instead.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/mail-back" className="btn btn-outline rounded-full">
              Request mail-back label
            </Link>
            <Link to="/verify" className="btn btn-outline rounded-full">
              Check another item
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          {results.count} bin{results.count === 1 ? '' : 's'} near {results.origin.zipCode}
        </h2>
        <span className="text-xs uppercase tracking-[0.16em] text-neutral">Within {results.radiusMiles} miles</span>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {results.results.map((bin) => (
          <BinCard key={bin.storeId} bin={bin} hasToken={hasToken} origin={results.origin} />
        ))}
      </div>
    </div>
  );
}

function BinCard({ bin, hasToken, origin }) {
  const mapUrl = useMemo(() => {
    if (!hasToken || !bin.longitude || !bin.latitude) return null;
    const center = `${bin.longitude},${bin.latitude}`;
    const userPin = origin?.lat && origin?.lon ? `pin-s+2563EB(${origin.lon},${origin.lat}),` : '';
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${userPin}pin-s+0A8A4F(${center})/${center},12,0/400x200?access_token=${MAPBOX_TOKEN}`;
  }, [bin.longitude, bin.latitude, origin?.lat, origin?.lon, hasToken]);

  return (
    <div className="card bg-base-100 shadow-lg">
      {mapUrl ? (
        <figure className="h-48 w-full overflow-hidden rounded-t-2xl">
          <img src={mapUrl} alt={`Map showing ${bin.storeName}`} className="h-full w-full object-cover" loading="lazy" />
        </figure>
      ) : null}
      <div className="card-body space-y-2">
        <h3 className="card-title text-xl text-primary">{bin.storeName}</h3>
        <p className="text-sm text-neutral">
          {bin.address}
          <br />
          {bin.city}, {bin.state} {bin.zipCode}
        </p>
        <p className="text-xs uppercase tracking-[0.16em] text-neutral">Hosted by {bin.memberName || 'Pact Partner'}</p>
        <div className="flex items-center justify-between text-sm text-neutral">
          <span>{bin.distanceMiles.toFixed(1)} miles away</span>
          {bin.storeCode ? <span>Store code: {bin.storeCode}</span> : null}
        </div>
      </div>
    </div>
  );
}

export default BinFinder;
